import React, { useState, useEffect } from 'react';
import { AlertTriangle, X, CheckCircle, RefreshCw } from 'lucide-react';
import { CustomerNote, NoteScope } from '../../../hooks/useCustomerNotes';
import { ConcludeNoteModal } from './ConcludeNoteModal';
import toast from 'react-hot-toast';

interface CustomerNotesBannerProps {
  customerId: string | undefined;
  onShowHistory: () => void;
  onShowAddNote: () => void;
  scope?: NoteScope;
  refreshTrigger?: number;
  wsNotes?: CustomerNote[];
  lightTheme?: boolean;
  // Sourced from the single useCustomerNotes instance owned by the parent (OperationalView) —
  // this component used to mount its own independent instance, duplicating every fetch.
  notes: CustomerNote[];
  loading: boolean;
  refreshing: boolean;
  concludeNote: (id: string, reason: string) => Promise<void>;
  refreshNotes: () => void;
}

export const CustomerNotesBanner: React.FC<CustomerNotesBannerProps> = ({
  customerId,
  onShowHistory,
  onShowAddNote,
  scope,
  refreshTrigger,
  wsNotes,
  lightTheme = false,
  notes: hookNotes,
  loading,
  refreshing,
  concludeNote,
  refreshNotes,
}) => {
  const [selectedNote, setSelectedNote] = useState<CustomerNote | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showConcludeModal, setShowConcludeModal] = useState(false);

  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      refreshNotes();
    }
  }, [refreshTrigger, refreshNotes]);

  const notes: CustomerNote[] = wsNotes !== undefined
    ? wsNotes.filter(n => {
        if (scope?.unit_id) return n.unit_id === scope.unit_id;
        if (scope?.department_id) return n.department_id === scope.department_id;
        if (scope?.person_id) return n.person_id === scope.person_id;
        return !n.unit_id && !n.department_id && !n.person_id;
      })
    : hookNotes;

  const isScoped = !!(scope?.unit_id || scope?.department_id);
  const scopeLabel = scope?.unit_id ? 'Avisos da Unidade' : scope?.department_id ? 'Avisos do Departamento' : 'Avisos do Cliente';

  const handleOpenDetail = (note: CustomerNote) => {
    setSelectedNote(note);
    setShowDetailModal(true);
  };

  const handleCloseDetail = () => {
    setShowDetailModal(false);
  };

  const handleOpenConclude = () => {
    setShowDetailModal(false);
    setShowConcludeModal(true);
  };

  const handleCloseConcludeModal = () => {
    setShowConcludeModal(false);
    setSelectedNote(null);
  };

  const handleConclude = async (reason: string) => {
    if (!selectedNote) return;
    await concludeNote(selectedNote.id, reason);
    toast.success('Aviso concluído');
  };

  if ((wsNotes === undefined && loading) || !customerId) return null;
  if (notes.length === 0) return null;

  const bannerBgClass = isScoped ? 'bg-orange-500/10' : 'bg-yellow-500/10';
  const bannerBorderClass = isScoped ? 'border-orange-500/50' : 'border-yellow-500/50';
  const iconColorClass = isScoped
    ? lightTheme ? 'text-orange-700' : 'text-orange-500'
    : lightTheme ? 'text-yellow-700' : 'text-yellow-500';
  const badgeBgClass = isScoped ? 'bg-orange-500' : 'bg-yellow-500';

  return (
    <div className={`${bannerBgClass} border-b-2 ${bannerBorderClass} px-3 py-2`}>
      <div className="flex flex-wrap items-center gap-1.5">
        {/* Label */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <AlertTriangle className={`h-4 w-4 ${iconColorClass}`} />
          <span className={`text-xs font-semibold ${iconColorClass} uppercase tracking-wide`}>
            {scopeLabel}
          </span>
          <span className={`${badgeBgClass} text-[black] text-xs font-bold px-1.5 py-0.5 rounded-full leading-none`}>
            {notes.length}
          </span>
          {refreshing && <RefreshCw className="h-3 w-3 animate-spin text-gray-400" />}
        </div>

        {/* Note chips */}
        {notes.map((note) => (
          <NoteChip
            key={note.id}
            note={note}
            onClick={() => handleOpenDetail(note)}
            isScoped={isScoped}
          />
        ))}
      </div>

      {/* Note Detail Modal */}
      <NoteDetailModal
        open={showDetailModal}
        note={selectedNote}
        isScoped={isScoped}
        onClose={handleCloseDetail}
        onConclude={handleOpenConclude}
      />

      {/* Conclude Note Modal */}
      <ConcludeNoteModal
        open={showConcludeModal}
        onClose={handleCloseConcludeModal}
        onSubmit={handleConclude}
        note={selectedNote}
      />
    </div>
  );
};

// ─── Note Chip ────────────────────────────────────────────────────────────────

interface NoteChipProps {
  note: CustomerNote;
  onClick: () => void;
  isScoped?: boolean;
}

const NoteChip: React.FC<NoteChipProps> = ({ note, onClick, isScoped = false }) => {
  const cardBgClass = isScoped ? 'bg-orange-100 hover:bg-orange-200' : 'bg-yellow-100 hover:bg-yellow-200';
  const cardBorderClass = isScoped ? 'border-orange-400' : 'border-yellow-400';
  const textColorClass = isScoped ? 'text-orange-900' : 'text-yellow-900';

  return (
    <button
      onClick={onClick}
      className={`${cardBgClass} border ${cardBorderClass} rounded-full pl-2.5 pr-2.5 py-0.5 max-w-xs transition-colors cursor-pointer`}
      title={note.content}
    >
      <span className={`${textColorClass} text-xs truncate block`}>
        {note.content}
      </span>
    </button>
  );
};

// ─── Note Detail Modal ────────────────────────────────────────────────────────

interface NoteDetailModalProps {
  open: boolean;
  note: CustomerNote | null;
  isScoped: boolean;
  onClose: () => void;
  onConclude: () => void;
}

const NoteDetailModal: React.FC<NoteDetailModalProps> = ({ open, note, isScoped, onClose, onConclude }) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  if (!open || !note) return null;

  const createdAt = new Date(note.created_at);
  const formattedDate = createdAt.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const formattedTime = createdAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  const accentBg = isScoped ? 'bg-orange-50 border-orange-300' : 'bg-yellow-50 border-yellow-300';
  const accentText = isScoped ? 'text-orange-900' : 'text-yellow-900';
  const concludeBg = isScoped ? 'bg-orange-500 hover:bg-orange-400 text-orange-950' : 'bg-yellow-500 hover:bg-yellow-400 text-yellow-950';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-md bg-white rounded-xl shadow-2xl border border-gray-200 max-h-[80vh] flex flex-col">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors z-10"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Scrollable content */}
        <div className="p-6 pt-5 pr-10 overflow-y-scroll flex-1 custom-scrollbar">
          {/* Message — hero */}
          <p className={`text-base font-medium whitespace-pre-wrap leading-relaxed mb-4 ${accentText}`}>
            {note.content}
          </p>

          {/* Author + date */}
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-semibold text-gray-800">{note.created_by_name}</span>
            <span className="text-gray-300">·</span>
            <span className="text-xs text-gray-400">{formattedDate} às {formattedTime}</span>
          </div>
        </div>

        {/* Fixed footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Fechar
          </button>
          <button
            onClick={onConclude}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${concludeBg}`}
          >
            <CheckCircle className="h-4 w-4" />
            Concluir Aviso
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerNotesBanner;
